import { useCallback, useEffect, useMemo } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { useOptionalAppConfig } from "../../../../../services/studio/AppConfigProvider";
import { classifySelectedImage } from "../services/classifySelection";
import { CLASSIFY_TRACE_SCOPE } from "../services/classifyImageApi";
import { devTrace } from "../../shared/services/devTrace";
import type { ClassifyImage } from "./usePptSelectionKind";

/**
 * Binds {@link classifySelectedImage} to this session's Deckorate HTTP client
 * and Studio client id, giving the dock the classifier that turns a plain
 * picture into a chart / table / portrait / sketch (AIVA-5376).
 *
 * Returns `undefined` — meaning "don't classify" — when the workspace hasn't
 * resolved a client id yet. The blob the classifier uploads is tenant-scoped, so
 * guessing an id would either fail the blob-sas request or, worse, write into
 * another tenant's container; keeping the generic image chips until the config
 * arrives is the safe answer. (Photo Headshot hardcodes `1` here with a note to
 * do exactly this instead; new code reads the real value.)
 */
export function useImageClassifier(): ClassifyImage | undefined {
  const http = useDeckorateHttp();
  const clientId = useOptionalAppConfig()?.data?.client.id;

  // The skip below is the feature's quietest failure: no request is made, no
  // error is logged, and the tray shows a correct-looking generic chip set — so
  // a workspace whose config hasn't arrived looks identical to one where
  // classification simply doesn't exist. Traced on change (not every render) so
  // the reason is visible while developing.
  useEffect(() => {
    devTrace(
      CLASSIFY_TRACE_SCOPE,
      typeof clientId === "number" ? "classifier ready" : "classifier DISABLED: no Studio client id yet",
      { clientId: clientId ?? null },
    );
  }, [clientId]);

  const classify = useCallback<ClassifyImage>(
    () =>
      // `clientId` is non-null wherever this is reachable — the memo below drops
      // the callback entirely when it is missing — but it is narrowed rather
      // than asserted so a future change can't smuggle an undefined tenant into
      // a blob path.
      typeof clientId === "number"
        ? classifySelectedImage({ http, clientId })
        : Promise.resolve(null),
    [http, clientId],
  );

  // Identity is stable while http and clientId are, so the selection hook's
  // effect doesn't re-subscribe on every render of the dock.
  return useMemo(() => (typeof clientId === "number" ? classify : undefined), [classify, clientId]);
}
